import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Introduction, RenameNft, YatModalSideSheet, YatNotVisualized } from '@pkg/components';
import { Error } from '@pkg/components/NFT/NftSidePanel/NftSteps';
import { Checkout } from '@pkg/components/NFT/NftSidePanel/NftSteps/Checkout';
import { Loading } from '@pkg/components/NFT/NftSidePanel/NftSteps/Loading';
import { NameNft } from '@pkg/components/NFT/NftSidePanel/NftSteps/NameNft';
import { NftMintComplete } from '@pkg/components/NFT/NftSidePanel/NftSteps/NftMintComplete';
import { Processing } from '@pkg/components/NFT/NftSidePanel/NftSteps/Processing';
import { RenameNftFeedback } from '@pkg/components/NFT/NftSidePanel/NftSteps/RenameNftFeedback';
import { YatMintedFeedback } from '@pkg/components/NFT/NftSidePanel/NftSteps/YatMintedFeedback';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { NftMintSteps } from '@app/client/common/structs';
import { setNftMintPanelStep, setNftPanelOpen } from '@app/store/actions/nft';

import { Typography } from '../../DesignSystem';

import './NftSidePanel.scss';

export const YatNftSidePanel = () => {
    const { t } = useTranslation(['nft']);
    const dispatch = useAppDispatch();
    const { currentEmoji, currentRhythmScore, currentEmojiMinted } = useAppSelector((state) => state.userEmojis);
    const { nftPanelOpen, nftMintPanelStep } = useAppSelector((state) => state.nft);
    const { visualizerFiles, visualizerMetadata, visualizerNameIsLocked, visualizerIsLocked } = useAppSelector(
        (state) => state.userEmojis,
    );

    const [step, setStep] = useState<NftMintSteps>();
    const [title, setTitle] = useState<React.ReactElement | null>();
    const [classList, setClassList] = useState<string>();

    const mintingInitiated = !!parseInt(localStorage.getItem('mintingInitiated') || '0');

    const handleStep = useCallback(
        (s) => {
            const headerContainer = (label: string) => {
                if (!label) {
                    return null;
                }
                return (
                    <div className="nft-sidebar-title">
                        <Typography type="headline" size={30}>
                            {label}
                        </Typography>
                    </div>
                );
            };
            let step;
            let title;
            const nftSidebarClassList = ['nft-sidebar'];
            switch (s) {
                case NftMintSteps.Checkout:
                    title = headerContainer(t('words:checkout'));
                    step = <Checkout />;
                    break;
                case NftMintSteps.Complete:
                    step = <NftMintComplete videoUrl={visualizerFiles?.video || ''} />;
                    break;
                case NftMintSteps.Error:
                    nftSidebarClassList.push('nft-sidebar-error');
                    step = <Error />;
                    break;
                case NftMintSteps.Loading:
                    nftSidebarClassList.push('nft-sidebar-loading');
                    step = <Loading hideCopy />;
                    break;
                case NftMintSteps.LoadingMint:
                    nftSidebarClassList.push('nft-sidebar-loading');
                    step = <Loading />;
                    break;
                case NftMintSteps.NameNft:
                    title = headerContainer(t('naming_your_nft'));
                    step = <NameNft />;
                    break;
                case NftMintSteps.Processing:
                    step = <Processing />;
                    break;
                case NftMintSteps.RenameNft:
                    title = headerContainer(t('rename_your_nft'));
                    step = <RenameNft />;
                    break;
                case NftMintSteps.RenameNftCancel:
                    nftSidebarClassList.push('nft-sidebar-rename-nft');
                    title = headerContainer(t('rename_your_nft'));
                    step = <RenameNftFeedback type="cancel" />;
                    break;
                case NftMintSteps.RenameNftSuccess:
                    nftSidebarClassList.push('nft-sidebar-rename-nft');
                    title = headerContainer(t('rename_your_nft'));
                    step = <RenameNftFeedback type="success" />;
                    break;

                case NftMintSteps.YatMintedFeedback:
                    nftSidebarClassList.push('nft-sidebar-minted-feedback');

                    title = headerContainer(t('minting_yat'));
                    step = <YatMintedFeedback />;
                    break;

                case NftMintSteps.YatNotVisualized:
                    nftSidebarClassList.push('nft-sidebar-not-visualized');
                    step = <YatNotVisualized currentEmoji={currentEmoji} currentRhythmScore={currentRhythmScore} />;
                    break;
                case NftMintSteps.Introduction:
                default:
                    step = <Introduction videoUrl={visualizerFiles?.video || ''} />;
            }

            if (step) {
                setStep(step);
            }
            setTitle(title);
            setClassList(nftSidebarClassList.join(' '));
        },
        [currentEmoji, currentRhythmScore, t, visualizerFiles?.video],
    );

    // TODO - Make Tech Debt card to clean this panel+steps up
    useEffect(() => {
        // Visualizer JSON files still being loaded
        if (visualizerNameIsLocked === null || visualizerIsLocked === null) {
            handleStep(NftMintSteps.Loading);
        }
        // Yat has not been visualized yet
        if (!visualizerMetadata && !visualizerFiles?.video) {
            handleStep(NftMintSteps.YatNotVisualized);
        }
        // Visualized but still being processed
        else if (!visualizerFiles?.video && visualizerMetadata) {
            handleStep(NftMintSteps.Processing);
        } else {
            if (!nftMintPanelStep) {
                handleStep(NftMintSteps.Introduction);
            } else {
                handleStep(nftMintPanelStep);
            }
        }

        if (!nftMintPanelStep && mintingInitiated) {
            handleStep(NftMintSteps.LoadingMint);
        }
        // Yat has already been minted
        if (currentEmojiMinted) {
            handleStep(NftMintSteps.Complete);
        }
    }, [
        currentEmojiMinted,
        handleStep,
        mintingInitiated,
        nftMintPanelStep,
        visualizerFiles,
        visualizerIsLocked,
        visualizerMetadata,
        visualizerNameIsLocked,
    ]);

    const onClose = () => {
        dispatch(setNftPanelOpen(false));
        dispatch(setNftMintPanelStep(NftMintSteps.Introduction));
    };

    return (
        <YatModalSideSheet
            classes={classList}
            closeCallback={() => onClose()}
            header={title || undefined}
            modalLabelId="yat-dashboard-nft-panel"
            open={nftPanelOpen}
            side="right"
        >
            <div id="yat-dashboard-nft-panel" key={`nft-panel-content`} className="yat-nft-sidebar">
                {step}
            </div>
        </YatModalSideSheet>
    );
};
